import type { BetterFetch } from "@better-fetch/fetch";
import { atom } from "nanostores";
import { z } from "zod";
import { adminEventEndpointContract, eventOccasionSchema } from "../../shared";
import type { EventOccasion } from "../../shared/types/event.types";
import type {
	AdminCategoryListQuery,
	AdminCreateOccasionBody,
	AdminOccasionListQuery,
	AdminUpdateOccasionBody,
	AdminUpdateOccasionsOrderBody,
} from "../../shared/types/eventApi.types";
import {
	createQuerySetMap,
	extractQueries,
	extractSignals,
} from "../shared/queryFactory";
import type { EndpointConfig, QuerySet } from "../shared/types";

const listEventsResponseSchema =
	adminEventEndpointContract.occasion.list.responseSchema;
const getEventResponseSchema =
	adminEventEndpointContract.occasion.read.responseSchema;
const listCategoriesResponseSchema =
	adminEventEndpointContract.category.list.responseSchema;

type ListEventsResponse = z.infer<typeof listEventsResponseSchema>;
type GetEventResponse = z.infer<typeof getEventResponseSchema>;
type ListCategoriesResponse = z.infer<typeof listCategoriesResponseSchema>;

type EventEndpointConfig = Record<string, EndpointConfig<unknown>>;

const buildEndpointConfig = (): EventEndpointConfig => ({
	listEvents: {
		path: adminEventEndpointContract.occasion.list.path,
		method: adminEventEndpointContract.occasion.list.method,
		responseSchema: listEventsResponseSchema,
	},
	getEvent: {
		path: adminEventEndpointContract.occasion.read.path,
		method: adminEventEndpointContract.occasion.read.method,
		responseSchema: getEventResponseSchema,
	},
	createEvent: {
		path: adminEventEndpointContract.occasion.create.path,
		method: adminEventEndpointContract.occasion.create.method,
		responseSchema: eventOccasionSchema,
	},
	updateEvent: {
		path: adminEventEndpointContract.occasion.update.path,
		method: adminEventEndpointContract.occasion.update.method,
		responseSchema: eventOccasionSchema,
	},
	deleteEvent: {
		path: adminEventEndpointContract.occasion.delete.path,
		method: adminEventEndpointContract.occasion.delete.method,
		responseSchema: z.object({ success: z.boolean() }),
	},
	publishEvent: {
		path: adminEventEndpointContract.occasion.publish.path,
		method: adminEventEndpointContract.occasion.publish.method,
		responseSchema: eventOccasionSchema,
	},
	listCategories: {
		path: adminEventEndpointContract.category.list.path,
		method: adminEventEndpointContract.category.list.method,
		responseSchema: listCategoriesResponseSchema,
	},
});

const replacePathParams = (
	path: string,
	params: Record<string, string | number>,
) =>
	path.replace(/:([a-zA-Z]+)/g, (_, key) => {
		const value = params[key];
		if (value === undefined) {
			throw new Error(`Missing path parameter: ${key}`);
		}
		return encodeURIComponent(String(value));
	});

export const createEventAdminPlugin = ($fetch: BetterFetch) => {
	const endpointConfig = buildEndpointConfig();
	const querySets = createQuerySetMap(endpointConfig, $fetch);

	const $selectedEvent = atom<EventOccasion | null>(null);
	const $isFormOpen = atom<boolean>(false);
	const $formMode = atom<"create" | "edit">("create");
	const $draftValues = atom<Partial<AdminCreateOccasionBody>>({});

	// Only invalidate queries for mutation endpoints to avoid infinite refetch loops on GET
	const mutationListeners = (
		Object.entries(endpointConfig).filter(
			([, cfg]) => cfg.method !== "GET",
		) as [string, EndpointConfig][]
	).map(
		([name]) => (querySets as Record<string, QuerySet<unknown>>)[name].listener,
	);

	return {
		signals: extractSignals(querySets),
		queries: extractQueries(querySets),
		listeners: mutationListeners,
		atoms: {
			$selectedEvent,
			$isFormOpen,
			$formMode,
			$draftValues,
		},
		actions: {
			async listEvents(query?: Partial<AdminOccasionListQuery>) {
				return $fetch<ListEventsResponse>(
					adminEventEndpointContract.occasion.list.path,
					{
						method: adminEventEndpointContract.occasion.list.method,
						query,
					},
				);
			},
			async getEvent(eventOccasionId: number) {
				return $fetch<GetEventResponse>(
					replacePathParams(adminEventEndpointContract.occasion.read.path, {
						eventOccasionId,
					}),
					{
						method: adminEventEndpointContract.occasion.read.method,
					},
				);
			},
			async createEvent(payload: AdminCreateOccasionBody) {
				return $fetch<EventOccasion>(
					adminEventEndpointContract.occasion.create.path,
					{
						method: adminEventEndpointContract.occasion.create.method,
						body: payload,
					},
				);
			},
			async updateEvent(
				eventOccasionId: number,
				payload: AdminUpdateOccasionBody,
			) {
				return $fetch<EventOccasion>(
					replacePathParams(adminEventEndpointContract.occasion.update.path, {
						eventOccasionId,
					}),
					{
						method: adminEventEndpointContract.occasion.update.method,
						body: payload,
					},
				);
			},
			async updateEventsOrder(payload: AdminUpdateOccasionsOrderBody) {
				return $fetch<{ success: boolean }>(
					adminEventEndpointContract.occasion.batchOrder.path,
					{
						method: adminEventEndpointContract.occasion.batchOrder.method,
						body: payload,
					},
				);
			},
			async deleteEvent(eventOccasionId: number) {
				return $fetch<{ success: boolean }>(
					replacePathParams(adminEventEndpointContract.occasion.delete.path, {
						eventOccasionId,
					}),
					{
						method: adminEventEndpointContract.occasion.delete.method,
					},
				);
			},
			async publishEvent(
				eventOccasionId: number,
				payload: { isPublished: boolean; publishedAt?: Date },
			) {
				return $fetch<EventOccasion>(
					replacePathParams(adminEventEndpointContract.occasion.publish.path, {
						eventOccasionId,
					}),
					{
						method: adminEventEndpointContract.occasion.publish.method,
						body: payload,
					},
				);
			},
			async listCategories(query?: Partial<AdminCategoryListQuery>) {
				return $fetch<ListCategoriesResponse>(
					adminEventEndpointContract.category.list.path,
					{
						method: adminEventEndpointContract.category.list.method,
						query,
					},
				);
			},
			async getGlobalSettings() {
				return $fetch(adminEventEndpointContract.settings.read.path, {
					method: adminEventEndpointContract.settings.read.method,
				});
			},
			async updateGlobalSettings(body: Record<string, unknown>) {
				return $fetch(adminEventEndpointContract.settings.update.path, {
					method: adminEventEndpointContract.settings.update.method,
					body,
				});
			},
			openForm(mode: "create" | "edit", event?: EventOccasion | null) {
				$formMode.set(mode);
				$isFormOpen.set(true);
				if (event) {
					const mappedEvent: EventOccasion = {
						...event,
						reservationStartAt: event.reservationStartAt
							? new Date(event.reservationStartAt)
							: null,
						reservationEndAt: event.reservationEndAt
							? new Date(event.reservationEndAt)
							: null,
						publishedAt: event.publishedAt ? new Date(event.publishedAt) : null,
						createdAt: event.createdAt ? new Date(event.createdAt) : undefined,
						updatedAt: event.updatedAt ? new Date(event.updatedAt) : undefined,
						deletedAt: event.deletedAt ? new Date(event.deletedAt) : null,
					} as EventOccasion;
					$selectedEvent.set(mappedEvent);
					$draftValues.set({
						eventCategoryId: event.eventCategoryId,
						title: event.title,
						subtitle: event.subtitle ?? undefined,
						description: event.description ?? undefined,
						membershipScope: event.membershipScope,
						allowNonMember: event.allowNonMember,
						isPaidEvent: event.isPaidEvent,
						price: event.price ?? undefined,
						currency: event.currency ?? undefined,
						isPublished: event.isPublished,
						reservationStartAt: event.reservationStartAt
							? new Date(event.reservationStartAt)
							: undefined,
						reservationEndAt: event.reservationEndAt
							? new Date(event.reservationEndAt)
							: undefined,
						allowMultipleBookings: event.allowMultipleBookings,
						groupBookingMode: event.groupBookingMode,
						displayOrder: event.displayOrder,
						fee: event.fee,
						cancelable: event.cancelable,
						cancelConditions: event.cancelConditions,
						cancelDescription: event.cancelDescription ?? undefined,
						isMessage: event.isMessage,
						messageRegisteredLineWorks:
							event.messageRegisteredLineWorks ?? undefined,
						messageCanceledLineWorks:
							event.messageCanceledLineWorks ?? undefined,
						categoryMessageDetail: event.categoryMessageDetail,
						emailsLineWorks: event.emailsLineWorks,
						heroImageUrl: event.heroImageUrl ?? undefined,
						gallery: event.gallery,
						isMultiEvent: event.isMultiEvent,
						notRegisterEventSameTime: event.notRegisterEventSameTime,
					});
				} else {
					$selectedEvent.set(null);
					$draftValues.set({});
				}
			},
			closeForm() {
				$isFormOpen.set(false);
			},
			resetDraft() {
				$draftValues.set({});
			},
		},
		querySets,
	};
};
